import { useSyncExternalStore } from 'react';

/**
 * Theme. Three states, not two: "system" is the default and must stay a real
 * option - a phone that flips to dark at dusk on the shop floor should follow,
 * and someone who has explicitly chosen light at 9pm should not be overridden.
 *
 * The class is applied by an inline script in index.html before first paint;
 * this module keeps it in step afterwards.
 */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'mt.theme';
const listeners = new Set<() => void>();

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

let current: Theme = read();

function apply(theme: Theme): void {
  const dark =
    theme === 'dark' ||
    (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
}

export function setTheme(theme: Theme): void {
  current = theme;
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    /* private mode - the choice just won't survive a reload */
  }
  apply(theme);
  for (const l of listeners) l();
}

/** Track the OS preference so "system" stays live rather than resolving once. */
export function initTheme(): () => void {
  const mq = matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (current === 'system') apply('system');
  };
  mq.addEventListener('change', onChange);
  apply(current);
  return () => mq.removeEventListener('change', onChange);
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => 'system' as Theme,
  );
  return [theme, setTheme];
}
